import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Phone, PhoneCall, PhoneOff, PhoneIncoming, PhoneOutgoing, Clock, CheckCircle, X, XCircle, AlertCircle, BarChart3, Calendar, Filter, ExternalLink, Copy, FileText, MessageSquare, ClipboardList, History, Sparkles, Users, ClipboardCheck } from 'lucide-react';
import { callReportService } from '../../services/callReportService';
import TranscriptionModal from '../../components/TranscriptionModal';
import DispositionModal from '../../components/DispositionModal';
import CallAuditModal from '../../components/CallAuditModal';
import CallRecordingPlayer from '../../components/CallRecordingPlayer';
import { transcriptionService } from '../../services/transcriptionService';
import AISummaryModal from '../../components/AISummaryModal';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env';

// Local date helpers (avoid UTC shifting issues with YYYY-MM-DD)
const pad2 = (n) => String(n).padStart(2, '0');
const toLocalYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toLocalHM = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const localDateTimeFromParts = (dateStr, timeStr, fallback) => {
  const ds = String(dateStr || '').trim();
  if (!ds) return fallback || null;
  const [y, m, d] = ds.split('-').map((v) => parseInt(v, 10));
  if (![y, m, d].every((v) => Number.isFinite(v))) return fallback || null;
  const ts = String(timeStr || '').trim();
  const [hh, min] = ts ? ts.split(':').map((v) => parseInt(v, 10)) : [0, 0];
  const H = Number.isFinite(hh) ? hh : 0;
  const M = Number.isFinite(min) ? min : 0;
  return new Date(y, (m || 1) - 1, d || 1, H, M, 0, 0);
};

const displayLocalDateTime = (dateStr, timeStr) => {
  const dt = localDateTimeFromParts(dateStr, timeStr, null);
  if (!dt || Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CallReport = ({
  complaintDetailsPathPrefix = '/service_advisor/complaints',
  showAIChat = false,
  scopeToCurrentUser = true,
  enableRecentCallsFilters = false,
}) => {
  const navigate = useNavigate();
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('roadserve_current_user') || 'null');
    } catch {
      return null;
    }
  })();
  const isAdminUser = String(currentUser?.role || currentUser?.primary_role || '')
    .toLowerCase()
    .includes('admin') || enableRecentCallsFilters;
  const agentCallReportId = currentUser?.call_report_id ?? null;
  const shouldScopeByAgent = Boolean(scopeToCurrentUser);
  const [dateFilter, setDateFilter] = useState({
    // Default: show only yesterday + today
    startDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return toLocalYMD(d);
    })(),
    startTime: '00:00',
    endDate: toLocalYMD(new Date()),
    endTime: toLocalHM(new Date()),
  });
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [dateTimeDraft, setDateTimeDraft] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
    return { start, end: now };
  });
  const [dateTimeError, setDateTimeError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [callTypeFilter, setCallTypeFilter] = useState('all');
  const [selectedCallForTranscription, setSelectedCallForTranscription] = useState(null);
  const [isTranscriptionModalOpen, setIsTranscriptionModalOpen] = useState(false);
  const [selectedCallForDisposition, setSelectedCallForDisposition] = useState(null);
  const [isDispositionModalOpen, setIsDispositionModalOpen] = useState(false);
  const [selectedCallForAudit, setSelectedCallForAudit] = useState(null);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [selectedCallForDetails, setSelectedCallForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAuditDrilldownOpen, setIsAuditDrilldownOpen] = useState(false);
  const [auditDrilldownFilter, setAuditDrilldownFilter] = useState('all'); // all | pass | needs_improvement | fail
  // Admin: Employee-wise audit drilldown
  const [isEmpAuditDrilldownOpen, setIsEmpAuditDrilldownOpen] = useState(false);
  const [empAuditDrilldownAgentId, setEmpAuditDrilldownAgentId] = useState('all'); // 'all' | agentId | 'unknown'
  const [empAuditDrilldownFilter, setEmpAuditDrilldownFilter] = useState('pending'); // all | total | audited | pending | pass | needs_improvement | fail
  const recentCallsSectionRef = useRef(null);
  const [recentHighlightCallId, setRecentHighlightCallId] = useState('');
  const [recordsLimit, setRecordsLimit] = useState(50); // 20 / 50 / 100
  const [currentPage, setCurrentPage] = useState(1); // 1-based
  const [kpiFilterKey, setKpiFilterKey] = useState('all'); // click-to-filter for Recent Calls
  // Recent Calls filters (admin-only, client-side)
  const [recentSearch, setRecentSearch] = useState('');
  const [recentLinkedFilter, setRecentLinkedFilter] = useState('all'); // all | linked | not_linked
  const [recentAgentFilter, setRecentAgentFilter] = useState('all'); // all | agentId
  const [recentAuditFilter, setRecentAuditFilter] = useState('all'); // all | pending | audited | pass | needs_improvement | fail
  const [autoTranscribeEnabled, setAutoTranscribeEnabled] = useState(() => {
    const v = localStorage.getItem('callReport:autoTranscribe');
    return v === null ? true : v === 'true';
  });
  const [autoTranscribeLastError, setAutoTranscribeLastError] = useState('');
  const [autoTranscribeLastSuccessAt, setAutoTranscribeLastSuccessAt] = useState('');
  // Only auto-transcribe NEW calls (do not backfill old recordings)
  // We "baseline" the current list of callids on first enable/mount, and only process callids not seen before.
  const autoSeenCallIdsRef = useRef(new Set());
  const autoSeenInitializedRef = useRef(false);

  // Auto queue (client-side)
  const autoQueueRef = useRef([]);
  const autoActiveRef = useRef(0);
  const autoInFlightRef = useRef(new Set());
  const [isAISummaryOpen, setIsAISummaryOpen] = useState(false);

  // Helper functions (defined before use)
  const getStatusLabel = (statusCode) => {
    const statusMap = {
      3: 'Both Answered',
      4: 'Customer Answered - Agent Unanswered',
      5: 'Customer Answered',
      6: 'Customer Unanswered - Agent Answered',
      7: 'Agent Unanswered',
      8: 'Customer Unanswered',
      9: 'Both Unanswered',
      10: 'Agent Answered',
      11: 'Rejected Call',
      12: 'Skipped',
      13: 'Agent Failed',
      14: 'Customer Failed - Agent Answered',
      15: 'Customer Failed',
      16: 'Customer Answered - Agent Failed',
      17: 'Agent Busy',
      18: 'Customer Answered - Agent Not Found',
      19: 'Customer Unanswered - Agent Busy',
    };
    return statusMap[statusCode] || `Unknown (${statusCode})`;
  };

  const getStatusColor = (statusCode) => {
    if ([3, 5, 6, 10].includes(statusCode)) return 'text-green-600 bg-green-50';
    if ([8, 9, 7].includes(statusCode)) return 'text-red-600 bg-red-50';
    if ([11, 12].includes(statusCode)) return 'text-yellow-600 bg-yellow-50';
    if ([13, 15, 16, 17, 18, 19].includes(statusCode)) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getCallTypeLabel = (type) => {
    const typeMap = {
      'IBD': 'Inbound',
      'OBD': 'Outbound',
      'CTC': 'Click to Call',
      'QC': 'Quick Call'
    };
    return typeMap[type] || type;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const hasAnyDisposition = (call) => {
    if (!call) return false;
    return Boolean(
      call.service_type ||
      call.disposition ||
      call.price ||
      call.city ||
      call.notes_detail ||
      call.disposition_category ||
      call.disposition_note
    );
  };

  const phoneLast10 = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    return digits.slice(-10);
  };

  // Fetch call reports
  const { data: callReports, isLoading, refetch } = useQuery({
    queryKey: ['callReports', dateFilter, statusFilter, callTypeFilter, shouldScopeByAgent ? (agentCallReportId || 'missing') : 'all'],
    queryFn: () => {
      // HARD SCOPE: if we must scope to current user but mapping is missing, show empty list.
      if (shouldScopeByAgent && !agentCallReportId) {
        return { success: true, data: [], error: null };
      }
      return callReportService.getCallReports(dateFilter, statusFilter, callTypeFilter, {
        agentCallReportId: shouldScopeByAgent ? agentCallReportId : null,
      });
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch users data for employee-wise breakdown via RPC (bypasses RLS)
  const { data: usersData } = useQuery({
    queryKey: ['users_for_call_report'],
    queryFn: async () => {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/rest/v1/rpc/get_agent_names_for_call_reports`,
          {
            method: 'POST',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
          }
        );
        if (!response.ok) {
          const txt = await response.text().catch(() => '');
          console.error('Failed to fetch agent names:', response.status, response.statusText, txt);
          return [];
        }
        const data = await response.json();
        return data || [];
      } catch (error) {
        console.error('Error fetching agent names:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Map call_report_id -> user name (for showing agent/employee name in call rows)
  const agentIdToNameMap = React.useMemo(() => {
    const m = new Map();
    (usersData || []).forEach((u) => {
      if (u?.call_report_id === null || u?.call_report_id === undefined) return;
      m.set(String(u.call_report_id), u?.name || 'Unknown');
    });
    return m;
  }, [usersData]);

  const getAgentIdForCall = (call) => {
    if (!call) return '';
    if (Array.isArray(call?.aansh) && call.aansh.length > 0) return String(call.aansh[0]);
    if (call?.masteragent !== null && call?.masteragent !== undefined) return String(call.masteragent);
    return '';
  };

  const getAgentNameForCall = (call) => {
    const id = getAgentIdForCall(call);
    if (!id) return '';
    return agentIdToNameMap.get(id) || `Agent ${id}`;
  };

  // Fetch service_request matches by phone number (for "Linked complaint" even when service_request_id is null)
  const { data: srMatchesData } = useQuery({
    queryKey: [
      'sr_matches_for_numbers',
      callReports?.data?.length || 0,
      dateFilter,
      statusFilter,
      callTypeFilter,
      shouldScopeByAgent ? (agentCallReportId || 'missing') : 'all',
    ],
    queryFn: async () => {
      try {
        const calls = callReports?.data || [];
        if (!calls.length) return [];

        const nums = Array.from(new Set(calls.map((c) => phoneLast10(c?.cnumber)).filter(Boolean))).slice(0, 500); // safety cap
        if (!nums.length) return [];

        const resp = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_service_request_matches_for_numbers`, {
          method: 'POST',
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ numbers: nums }),
        });

        if (!resp.ok) {
          const txt = await resp.text().catch(() => '');
          console.error('Failed to fetch SR matches:', resp.status, resp.statusText, txt);
          return [];
        }
        const data = await resp.json();
        return data || [];
      } catch (e) {
        console.error('SR match fetch error:', e);
        return [];
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const srMatchByPhone = React.useMemo(() => {
    const m = new Map();
    (srMatchesData || []).forEach((r) => {
      if (r?.phone_last10) m.set(String(r.phone_last10), r);
    });
    return m;
  }, [srMatchesData]);

  const getLinkedServiceRequest = (call) => {
    if (!call) return null;
    // prefer explicit link (join)
    if (call?.service_requests?.id) return call.service_requests;
    const p10 = phoneLast10(call?.cnumber);
    if (!p10) return null;
    const m = srMatchByPhone.get(p10);
    if (!m) return null;
    // normalize shape similar to joined service_requests
    return {
      id: m.service_request_id,
      customer_name: m.customer_name,
      vehicle_number: m.vehicle_number,
      problem: m.problem,
      complaint_status: m.complaint_status,
      contact_number: call?.cnumber || null,
      emp_name: m.emp_name || null,
      pincode: m.pincode || null,
      request_status: m.request_status || null,
      complaint_registered_at: m.complaint_registered_at || null,
    };
  };

  // Reset pagination when filters/limit change
  useEffect(() => {
    setCurrentPage(1);
    setKpiFilterKey('all'); // reset any KPI filter when primary filters change
    if (enableRecentCallsFilters) {
      setRecentSearch('');
      setRecentLinkedFilter('all');
      setRecentAgentFilter('all');
    }
  }, [dateFilter.startDate, dateFilter.startTime, dateFilter.endDate, dateFilter.endTime, statusFilter, callTypeFilter, recordsLimit]);

  // Reset pagination when Recent Calls filters change (admin only)
  useEffect(() => {
    if (!enableRecentCallsFilters) return;
    setCurrentPage(1);
  }, [enableRecentCallsFilters, recentSearch, recentLinkedFilter, recentAgentFilter]);

  const openDateTimeModal = () => {
    setDateTimeError('');
    const now = new Date();
    const startFallback = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const start = localDateTimeFromParts(dateFilter.startDate, dateFilter.startTime, startFallback);
    const end = localDateTimeFromParts(dateFilter.endDate, dateFilter.endTime, now);
    setDateTimeDraft({ start, end });
    setShowDateTimeModal(true);
  };

  const closeDateTimeModal = () => {
    setShowDateTimeModal(false);
    setDateTimeError('');
  };

  const setPreset = (preset) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    if (preset === 'today') {
      setDateTimeDraft({ start: startOfToday, end: now });
      return;
    }
    if (preset === 'yesterday') {
      const y = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
      const end = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 0, 0);
      setDateTimeDraft({ start: y, end });
      return;
    }
    if (preset === 'last7') {
      const start = new Date(startOfToday.getTime() - 6 * 24 * 60 * 60 * 1000);
      setDateTimeDraft({ start, end: now });
    }
  };

  const toggleKpiFilter = (key) => {
    setKpiFilterKey((prev) => (prev === key ? 'all' : key));
    setCurrentPage(1);
  };

  const kpiFilteredCalls = React.useMemo(() => {
    const all = callReports?.data || [];
    if (kpiFilterKey === 'all') return all;

    // Helpers for fresh/repeat per number (within current filtered dataset / date range)
    const callsWithPhone = all
      .map((c) => ({ c, p10: phoneLast10(c?.cnumber) }))
      .filter((x) => Boolean(x.p10));
    const byPhone = new Map();
    for (const { c, p10 } of callsWithPhone) {
      const arr = byPhone.get(p10) || [];
      arr.push(c);
      byPhone.set(p10, arr);
    }
    const sortByTimeAsc = (a, b) => new Date(a?.createdat || a?.ivrstime || 0) - new Date(b?.createdat || b?.ivrstime || 0);
    const firstCallIds = new Set();
    for (const arr of byPhone.values()) {
      const sorted = [...arr].sort(sortByTimeAsc);
      const first = sorted[0];
      if (first?.callid) firstCallIds.add(first.callid);
    }

    if (kpiFilterKey === 'fresh_calls') {
      // include: first call per phone + calls without phone
      return all.filter((c) => {
        const p10 = phoneLast10(c?.cnumber);
        if (!p10) return true;
        return Boolean(c?.callid && firstCallIds.has(c.callid));
      });
    }
    if (kpiFilterKey === 'repeat_calls') {
      // include: calls for phones with 2+ calls, excluding the first one
      const repeatCallIds = new Set();
      for (const arr of byPhone.values()) {
        if (arr.length <= 1) continue;
        const sorted = [...arr].sort(sortByTimeAsc);
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i]?.callid) repeatCallIds.add(sorted[i].callid);
        }
      }
      return all.filter((c) => Boolean(c?.callid && repeatCallIds.has(c.callid)));
    }
    if (kpiFilterKey === 'transcribe_completed') {
      return all.filter((c) => String(c?.transcription_status || '').toLowerCase() === 'completed');
    }
    if (kpiFilterKey === 'transcribe_failed') {
      return all.filter((c) => String(c?.transcription_status || '').toLowerCase() === 'failed');
    }
    if (kpiFilterKey === 'linked') {
      // Fresh linked = first call per number that is linked (avoid counting repeat calls)
      return all.filter((c) => {
        const p10 = phoneLast10(c?.cnumber);
        const isLinked = Boolean(getLinkedServiceRequest(c)?.id);
        if (!p10) return isLinked;
        return Boolean(isLinked && c?.callid && firstCallIds.has(c.callid));
      });
    }
    if (kpiFilterKey === 'fresh_not_linked') {
      // Fresh not linked = first call per number that is NOT linked
      return all.filter((c) => {
        const p10 = phoneLast10(c?.cnumber);
        const isLinked = Boolean(getLinkedServiceRequest(c)?.id);
        if (!p10) return !isLinked;
        return Boolean(!isLinked && c?.callid && firstCallIds.has(c.callid));
      });
    }
    if (kpiFilterKey === 'answered') {
      return all.filter((c) => [3, 5, 6, 10].includes(Number(c?.callstatus ?? c?.callStatus)));
    }
    return all;
  }, [callReports?.data, kpiFilterKey, srMatchByPhone]);

  const recentCallsFiltered = React.useMemo(() => {
    const base = kpiFilteredCalls || [];
    if (!enableRecentCallsFilters) return base;

    const q = String(recentSearch || '').trim().toLowerCase();
    const wantsLinked = recentLinkedFilter === 'linked';
    const wantsNotLinked = recentLinkedFilter === 'not_linked';
    const agentFilter = String(recentAgentFilter || 'all');
    const auditFilter = String(recentAuditFilter || 'all').toLowerCase();
    const isAudited = (c) => Boolean(c?.audit_updated_at || c?.audit_status || c?.audit_score || c?.audit_feedback);

    const matchesSearch = (call) => {
      if (!q) return true;
      const agentName = (getAgentNameForCall(call) || '').toLowerCase();
      const cnum = String(call?.cnumber || '').replace(/\D/g, '');
      const last10 = phoneLast10(call?.cnumber);
      const callid = String(call?.callid || '').toLowerCase();
      const city = String(call?.city || '').toLowerCase();
      const serviceType = String(call?.service_type || call?.serviceType || '').toLowerCase();
      const disposition = String(call?.disposition || call?.disposition_category || '').toLowerCase();
      const linkedSr = getLinkedServiceRequest(call);
      const custName = String(linkedSr?.customer_name || '').toLowerCase();
      const vehicle = String(linkedSr?.vehicle_number || '').toLowerCase();

      return (
        agentName.includes(q) ||
        callid.includes(q) ||
        city.includes(q) ||
        serviceType.includes(q) ||
        disposition.includes(q) ||
        custName.includes(q) ||
        vehicle.includes(q) ||
        cnum.includes(q) ||
        last10.includes(q)
      );
    };

    return base.filter((call) => {
      if (agentFilter !== 'all') {
        const aid = getAgentIdForCall(call);
        if (String(aid) !== agentFilter) return false;
      }

      if (wantsLinked || wantsNotLinked) {
        const isLinked = Boolean(getLinkedServiceRequest(call)?.id);
        if (wantsLinked && !isLinked) return false;
        if (wantsNotLinked && isLinked) return false;
      }

      if (auditFilter !== 'all') {
        const audited = isAudited(call);
        const st = String(call?.audit_status || '').toLowerCase();
        if (auditFilter === 'pending' && audited) return false;
        if (auditFilter === 'audited' && !audited) return false;
        if (auditFilter === 'pass' && !(audited && st === 'pass')) return false;
        if (auditFilter === 'needs_improvement' && !(audited && st === 'needs_improvement')) return false;
        if (auditFilter === 'fail' && !(audited && st === 'fail')) return false;
      }

      return matchesSearch(call);
    });
  }, [
    enableRecentCallsFilters,
    kpiFilteredCalls,
    recentSearch,
    recentLinkedFilter,
    recentAgentFilter,
    recentAuditFilter,
    srMatchByPhone,
    agentIdToNameMap,
  ]);

  const recentAgentOptions = React.useMemo(() => {
    if (!enableRecentCallsFilters) return [];
    const all = kpiFilteredCalls || [];
    const ids = Array.from(
      new Set(
        all
          .map((c) => getAgentIdForCall(c))
          .filter((x) => Boolean(x))
          .map((x) => String(x))
      )
    );
    const items = ids
      .map((id) => ({ id, name: agentIdToNameMap.get(id) || `Agent ${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [enableRecentCallsFilters, kpiFilteredCalls, agentIdToNameMap]);

  const kpiFilterLabel = React.useMemo(() => {
    const map = {
      all: 'All Calls',
      fresh_calls: 'Fresh Calls (first per number)',
      repeat_calls: 'Repeat Calls',
      transcribe_completed: 'Transcribe Completed',
      transcribe_failed: 'Transcribe Failed',
      fresh_not_linked: 'Fresh Not Linked (first per number)',
      linked: 'Fresh Linked (first per number)',
      answered: 'Answered Calls',
    };
    return map[kpiFilterKey] || 'All Calls';
  }, [kpiFilterKey]);

  // Auto-transcribe new calls while on this page (client-side)
  useEffect(() => {
    localStorage.setItem('callReport:autoTranscribe', String(autoTranscribeEnabled));
    if (!autoTranscribeEnabled) return;

    const all = callReports?.data || [];
    // Baseline current calls so we don't transcribe old backlog.
    if (!autoSeenInitializedRef.current) {
      autoSeenCallIdsRef.current = new Set(
        all.map((c) => c?.callid).filter(Boolean)
      );
      autoSeenInitializedRef.current = true;
      return;
    }

    const candidates = all.filter((c) => {
      // Only newly arrived calls after baselining
      if (c?.callid && autoSeenCallIdsRef.current.has(c.callid)) return false;
      const hasRec = c?.recordings?.length > 0 && c?.recordings?.[0]?.file;
      const done = Boolean(c?.summary || c?.transcription);
      const status = String(c?.transcription_status || 'pending').toLowerCase();
      const blocked = status === 'processing' || status === 'completed' || status === 'failed';
      const hasError = Boolean(c?.transcription_error);

      // Cheap safety to reduce accidental bills: skip very short calls in auto mode (manual still allowed)
      const durationSec = Number(c?.talkduration ?? c?.ivrduration ?? 0) || 0;
      const longEnough = durationSec >= 12;

      return hasRec && !done && !blocked && !hasError && longEnough;
    });

    candidates
      .sort((a, b) => new Date(a.createdat || 0).getTime() - new Date(b.createdat || 0).getTime())
      .forEach(enqueueAutoTranscribe);
  }, [callReports?.data, autoTranscribeEnabled]);

  // Pagination (client-side)
  const totalCallsCount = recentCallsFiltered.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalCallsCount / recordsLimit));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * recordsLimit;
  const endIndexExclusive = startIndex + recordsLimit;
  const pageStartNumber = totalCallsCount === 0 ? 0 : startIndex + 1;
  const pageEndNumber = Math.min(endIndexExclusive, totalCallsCount);

  const visibleCalls = React.useMemo(() => {
    return recentCallsFiltered.slice(startIndex, endIndexExclusive);
  }, [recentCallsFiltered, startIndex, endIndexExclusive]);

  // Build quick lookup of "previous noted" by service_request_id (preferred) and by phone last10 (fallback)
  const prevDispositionLookup = React.useMemo(() => {
    const all = callReports?.data || [];
    const byServiceRequest = new Map();
    const byPhone = new Map();

    const score = (c) => {
      const t1 = c?.disposition_updated_at ? new Date(c.disposition_updated_at).getTime() : 0;
      const t2 = c?.createdat ? new Date(c.createdat).getTime() : 0;
      return Math.max(t1, t2);
    };

    for (const c of all) {
      if (!hasAnyDisposition(c)) continue;

      if (c.service_request_id) {
        const existing = byServiceRequest.get(c.service_request_id);
        if (!existing || score(c) > score(existing)) {
          byServiceRequest.set(c.service_request_id, c);
        }
      }

      const p10 = phoneLast10(c.cnumber);
      if (p10) {
        const existing = byPhone.get(p10);
        if (!existing || score(c) > score(existing)) {
          byPhone.set(p10, c);
        }
      }
    }

    return { byServiceRequest, byPhone };
  }, [callReports?.data]);

  const hasPreviousNoteForCall = (call) => {
    if (!call) return false;
    // If this call already has a note, no need to show "previous"
    if (hasAnyDisposition(call)) return false;

    // Prefer same complaint (service_request_id)
    if (call.service_request_id && prevDispositionLookup.byServiceRequest.has(call.service_request_id)) {
      return true;
    }

    // Fallback: same customer number last-10
    const p10 = phoneLast10(call.cnumber);
    return Boolean(p10 && prevDispositionLookup.byPhone.has(p10));
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!callReports || !callReports.data) return null;

    const calls = callReports.data;
    const total = calls.length;
    
    const statusCounts = {};
    const typeCounts = {};
    let totalDuration = 0;
    let answeredCalls = 0;
    let unansweredCalls = 0;

    calls.forEach(call => {
      // Status counts
      const status = getStatusLabel(call.callstatus);
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      // Type counts
      const type = call.ctype || 'Unknown';
      typeCounts[type] = (typeCounts[type] || 0) + 1;

      // Duration
      if (call.talkduration) {
        totalDuration += call.talkduration;
      }

      // Answered/Unanswered
      if (call.callstatus === 3 || call.callstatus === 5 || call.callstatus === 6 || call.callstatus === 10) {
        answeredCalls++;
      } else {
        unansweredCalls++;
      }
    });

    return {
      total,
      statusCounts,
      typeCounts,
      totalDuration,
      answeredCalls,
      unansweredCalls,
      averageDuration: total > 0 ? Math.round(totalDuration / total) : 0
    };
  }, [callReports]);

  // Date-range KPIs (based on current filtered dataset)
  const rangeKpis = React.useMemo(() => {
    const calls = callReports?.data || [];
    const total = calls.length;

    // Repeat vs Fresh (by customer phone last-10 within selected range)
    const byPhone = new Map();
    let noPhoneCount = 0;
    for (const c of calls) {
      const p10 = phoneLast10(c?.cnumber);
      if (!p10) {
        noPhoneCount += 1;
        continue;
      }
      const arr = byPhone.get(p10) || [];
      arr.push(c);
      byPhone.set(p10, arr);
    }

    const sortByTimeAsc = (a, b) => new Date(a?.createdat || a?.ivrstime || 0) - new Date(b?.createdat || b?.ivrstime || 0);
    const firstCalls = [];
    for (const arr of byPhone.values()) {
      const sorted = [...arr].sort(sortByTimeAsc);
      if (sorted[0]) firstCalls.push(sorted[0]);
    }

    const repeatCalls = Array.from(byPhone.values()).reduce((sum, arr) => sum + Math.max(0, arr.length - 1), 0);
    const freshCalls = firstCalls.length + noPhoneCount; // no-phone calls treated as separate fresh leads

    // Transcription status
    let transcribeCompleted = 0;
    let transcribeFailed = 0;
    for (const c of calls) {
      const st = String(c?.transcription_status || '').toLowerCase();
      if (st === 'completed') transcribeCompleted += 1;
      if (st === 'failed') transcribeFailed += 1;
    }

    // Fresh-linked should be based on FIRST call per number only (unique lead),
    // so repeat calls from same number don't inflate "linked".
    const freshLinked = firstCalls.filter((c) => Boolean(getLinkedServiceRequest(c)?.id)).length;
    const freshNotLinked = (firstCalls.length - freshLinked) + noPhoneCount;

    return {
      total,
      freshCalls,
      repeatCalls,
      transcribeCompleted,
      transcribeFailed,
      freshLinked,
      freshNotLinked,
    };
  }, [callReports?.data, srMatchByPhone]);

  // Employee-wise breakdown
  const employeeBreakdown = React.useMemo(() => {
    const calls = callReports?.data || [];
    const users = usersData || [];
    
    // Create a map of call_report_id -> user name
    const agentIdToName = new Map();
    users.forEach((u) => {
      if (u.call_report_id) {
        const idStr = String(u.call_report_id);
        agentIdToName.set(idStr, u.name || 'Unknown');
      }
    });

    // Group calls by agent ID
    const byAgent = new Map();
    
    calls.forEach((c) => {
      // Extract agent ID from aansh array or masteragent
      let agentId = null;
      if (Array.isArray(c.aansh) && c.aansh.length > 0) {
        agentId = String(c.aansh[0]);
      } else if (c.masteragent) {
        agentId = String(c.masteragent);
      }
      
      if (!agentId) return;
      
      if (!byAgent.has(agentId)) {
        byAgent.set(agentId, []);
      }
      byAgent.get(agentId).push(c);
    });

    // Calculate stats for each agent
    const breakdown = [];
    byAgent.forEach((agentCalls, agentId) => {
      const name = agentIdToName.get(agentId) || `Agent ${agentId}`;
      
      // Calculate fresh/repeat for this agent
      const byPhone = new Map();
      let noPhoneCount = 0;
      agentCalls.forEach((c) => {
        const p10 = phoneLast10(c?.cnumber);
        if (!p10) {
          noPhoneCount += 1;
          return;
        }
        const arr = byPhone.get(p10) || [];
        arr.push(c);
        byPhone.set(p10, arr);
      });

      const sortByTimeAsc = (a, b) => new Date(a?.createdat || a?.ivrstime || 0) - new Date(b?.createdat || b?.ivrstime || 0);
      const firstCalls = [];
      for (const arr of byPhone.values()) {
        const sorted = [...arr].sort(sortByTimeAsc);
        if (sorted[0]) firstCalls.push(sorted[0]);
      }

      const freshCalls = firstCalls.length + noPhoneCount;
      const repeatCalls = Array.from(byPhone.values()).reduce((sum, arr) => sum + Math.max(0, arr.length - 1), 0);
      const linkedComplaint = firstCalls.filter((c) => Boolean(getLinkedServiceRequest(c)?.id)).length;

      breakdown.push({
        agentId,
        name,
        freshCalls,
        repeatCalls,
        linkedComplaint,
        total: agentCalls.length,
      });
    });

    // Sort by total calls (descending)
    breakdown.sort((a, b) => b.total - a.total);
    
    return breakdown;
  }, [callReports?.data, usersData, srMatchByPhone]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle different timestamp formats
      // Database returns: "2025-12-15T15:58:34.678" (UTC)
      // Need to show in IST (UTC + 5:30)
      
      let date;
      if (dateString.includes('T')) {
        // ISO format - parse as UTC if has Z, otherwise as local
        if (dateString.endsWith('Z')) {
          date = new Date(dateString); // UTC timestamp
        } else {
          date = new Date(dateString + 'Z'); // Force UTC interpretation
        }
      } else {
        // PostgreSQL format "2025-12-15 15:58:34" - treat as UTC
        date = new Date(dateString.replace(' ', 'T') + 'Z');
      }
      
      if (isNaN(date.getTime())) return 'N/A';
      
      // Format in IST
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch (error) {
      console.error('Date formatting error:', error, dateString);
      return 'N/A';
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'N/A';
    // Remove country code (+91, 91) and show 10 digit number
    let cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.startsWith('91') && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }
    return cleaned.substring(0, 10);
  };

  const handleCopyPhone = (phoneNumber) => {
    const cleaned = formatPhoneNumber(phoneNumber);
    navigator.clipboard.writeText(cleaned);
    alert('Phone number copied!');
  };

  const handleCallNumber = (phoneNumber) => {
    const cleaned = formatPhoneNumber(phoneNumber);
    window.location.href = `tel:${cleaned}`;
  };

  const handleTranscribeClick = (call) => {
    // Manual click = allow retry; clear global banner so the UI isn't noisy.
    setAutoTranscribeLastError('');
    setSelectedCallForTranscription(call);
    setIsTranscriptionModalOpen(true);
  };

  const handleDispositionClick = (call) => {
    setSelectedCallForDisposition(call);
    setIsDispositionModalOpen(true);
  };

  const handleAuditClick = (call) => {
    setSelectedCallForAudit(call);
    setIsAuditModalOpen(true);
  };

  const handleDispositionSave = () => {
    refetch(); // Refresh the call list
  };

  const handleAuditSave = () => {
    refetch(); // Refresh the call list
  };

  const openDetails = (call) => {
    setSelectedCallForDetails(call || null);
    setIsDetailsModalOpen(true);
  };

  const allCallsForAudit = React.useMemo(() => callReports?.data || [], [callReports?.data]);
  const auditedCalls = React.useMemo(() => {
    const calls = allCallsForAudit;
    return calls.filter(
      (c) => Boolean(c?.audit_updated_at || c?.audit_status || c?.audit_score || c?.audit_feedback)
    );
  }, [allCallsForAudit]);
  const auditSummary = React.useMemo(() => {
    const calls = allCallsForAudit;
    const audited = calls.filter(
      (c) => Boolean(c?.audit_updated_at || c?.audit_status || c?.audit_score || c?.audit_feedback)
    );
    const pass = audited.filter((c) => String(c?.audit_status || '').toLowerCase() === 'pass').length;
    const fail = audited.filter((c) => String(c?.audit_status || '').toLowerCase() === 'fail').length;
    const needs = audited.filter((c) => String(c?.audit_status || '').toLowerCase() === 'needs_improvement').length;
    const scores = audited
      .map((c) => (c?.audit_score === null || c?.audit_score === undefined || c?.audit_score === '' ? null : Number(c.audit_score)))
      .filter((n) => typeof n === 'number' && !Number.isNaN(n));
    const avgScore = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    return {
      totalAudited: audited.length,
      pass,
      needs,
      fail,
      avgScore,
    };
  }, [allCallsForAudit]);

  // Employee-wise audit summary (admin audits)
  const auditEmployeeBreakdown = React.useMemo(() => {
    const calls = allCallsForAudit || [];
    const byAgent = new Map();

    const isAudited = (c) => Boolean(c?.audit_updated_at || c?.audit_status || c?.audit_score || c?.audit_feedback);
    const normStatus = (s) => String(s || '').toLowerCase();

    for (const c of calls) {
      const agentId = getAgentIdForCall(c) || 'unknown';
      const name =
        agentId === 'unknown'
          ? 'Unknown Agent'
          : agentIdToNameMap.get(String(agentId)) || `Agent ${String(agentId)}`;

      if (!byAgent.has(agentId)) {
        byAgent.set(agentId, {
          agentId,
          name,
          total: 0,
          audited: 0,
          pending: 0,
          pass: 0,
          needs: 0,
          fail: 0,
          scores: [],
        });
      }

      const row = byAgent.get(agentId);
      row.total += 1;

      if (isAudited(c)) {
        row.audited += 1;
        const st = normStatus(c?.audit_status);
        if (st === 'pass') row.pass += 1;
        else if (st === 'needs_improvement') row.needs += 1;
        else if (st === 'fail') row.fail += 1;

        const sc =
          c?.audit_score === null || c?.audit_score === undefined || c?.audit_score === ''
            ? null
            : Number(c.audit_score);
        if (typeof sc === 'number' && !Number.isNaN(sc)) row.scores.push(sc);
      } else {
        row.pending += 1;
      }
    }

    const rows = Array.from(byAgent.values()).map((r) => {
      const avgScore = r.scores.length ? r.scores.reduce((a, b) => a + b, 0) / r.scores.length : null;
      return { ...r, avgScore };
    });

    rows.sort((a, b) => {
      if (b.pending !== a.pending) return b.pending - a.pending;
      return b.total - a.total;
    });

    const totals = rows.reduce(
      (acc, r) => {
        acc.total += r.total;
        acc.audited += r.audited;
        acc.pending += r.pending;
        return acc;
      },
      { total: 0, audited: 0, pending: 0 }
    );

    return { rows, totals };
  }, [allCallsForAudit, agentIdToNameMap]);

  const openAuditDrilldown = (filterKey) => {
    setAuditDrilldownFilter(filterKey || 'all');
    setIsAuditDrilldownOpen(true);
  };

  const openEmpAuditDrilldown = (agentId, filterKey) => {
    setEmpAuditDrilldownAgentId(agentId || 'all');
    setEmpAuditDrilldownFilter(filterKey || 'all');
    setIsEmpAuditDrilldownOpen(true);
  };

  const jumpToRecentCall = (call) => {
    if (!call) return;
    // Ensure admin-only recent filters are available
    if (enableRecentCallsFilters) {
      const agentId = getAgentIdForCall(call) || 'all';
      const phone = phoneLast10(call?.cnumber) || '';
      if (agentId) setRecentAgentFilter(String(agentId));
      if (phone) setRecentSearch(phone);
      setRecentLinkedFilter('all');
    }
    setKpiFilterKey('all');
    setCurrentPage(1);

    const callId = String(call?.callid || '');
    if (callId) {
      setRecentHighlightCallId(callId);
      setTimeout(() => setRecentHighlightCallId((prev) => (prev === callId ? '' : prev)), 4500);
    }

    // Close drilldown and scroll to Recent Calls
    setIsEmpAuditDrilldownOpen(false);
    setTimeout(() => {
      try {
        recentCallsSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        if (callId) {
          const el = document.getElementById(`recent-call-${callId}`);
          el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        }
      } catch {}
    }, 50);
  };

  // Click in audit summary should NOT open any modal; just apply filters in Recent Calls.
  const applyAuditSummaryFilterToRecentCalls = (agentId, filterKey) => {
    if (!enableRecentCallsFilters) return;

    const agent = String(agentId || 'all');
    const key = String(filterKey || 'all').toLowerCase();

    // Map old keys to recentAuditFilter values
    const auditKey =
      key === 'total'
        ? 'all'
        : key === 'all'
          ? 'all'
          : key === 'audited'
            ? 'audited'
            : key === 'pending'
              ? 'pending'
              : key === 'pass'
                ? 'pass'
                : key === 'needs_improvement'
                  ? 'needs_improvement'
                  : key === 'fail'
                    ? 'fail'
                    : 'all';

    setKpiFilterKey('all');
    setCurrentPage(1);
    setRecentSearch('');
    setRecentLinkedFilter('all');
    setRecentAgentFilter(agent);
    setRecentAuditFilter(auditKey);

    setTimeout(() => {
      try {
        recentCallsSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      } catch {}
    }, 50);
  };

  // Employee-wise performance clicks should apply KPI filter + agent filter and scroll to Recent Calls (no modal)
  const applyEmployeePerformanceFilterToRecentCalls = (agentId, kpiKey) => {
    if (!enableRecentCallsFilters) return;
    const agent = String(agentId || 'all');
    const kpi = String(kpiKey || 'all');

    setCurrentPage(1);
    setRecentSearch('');
    setRecentLinkedFilter('all');
    setRecentAuditFilter('all');
    setRecentAgentFilter(agent);
    setKpiFilterKey(kpi);

    setTimeout(() => {
      try {
        recentCallsSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      } catch {}
    }, 50);
  };

  const drilldownCalls = React.useMemo(() => {
    const key = String(auditDrilldownFilter || 'all').toLowerCase();
    if (key === 'all') return auditedCalls;
    return auditedCalls.filter((c) => String(c?.audit_status || '').toLowerCase() === key);
  }, [auditedCalls, auditDrilldownFilter]);

  const drilldownTitle = React.useMemo(() => {
    const key = String(auditDrilldownFilter || 'all').toLowerCase();
    if (key === 'pass') return 'Pass Audits';
    if (key === 'needs_improvement') return 'Needs Improvement Audits';
    if (key === 'fail') return 'Fail Audits';
    return 'All Audited Calls';
  }, [auditDrilldownFilter]);

  const empAuditDrilldownCalls = React.useMemo(() => {
    const calls = allCallsForAudit || [];
    const agentKey = String(empAuditDrilldownAgentId || 'all');
    const filterKey = String(empAuditDrilldownFilter || 'all').toLowerCase();
    const isAudited = (c) => Boolean(c?.audit_updated_at || c?.audit_status || c?.audit_score || c?.audit_feedback);

    const matchesAgent = (c) => {
      if (agentKey === 'all') return true;
      const aid = getAgentIdForCall(c) || 'unknown';
      return String(aid) === agentKey;
    };

    const matchesFilter = (c) => {
      const audited = isAudited(c);
      const st = String(c?.audit_status || '').toLowerCase();
      if (filterKey === 'all' || filterKey === 'total') return true;
      if (filterKey === 'audited') return audited;
      if (filterKey === 'pending') return !audited;
      if (filterKey === 'pass') return audited && st === 'pass';
      if (filterKey === 'needs_improvement') return audited && st === 'needs_improvement';
      if (filterKey === 'fail') return audited && st === 'fail';
      return true;
    };

    const out = calls.filter((c) => matchesAgent(c) && matchesFilter(c));
    // Newest first
    out.sort((a, b) => new Date(b?.createdat || b?.ivrstime || 0) - new Date(a?.createdat || a?.ivrstime || 0));
    return out;
  }, [allCallsForAudit, empAuditDrilldownAgentId, empAuditDrilldownFilter]);

  const empAuditDrilldownTitle = React.useMemo(() => {
    const agentKey = String(empAuditDrilldownAgentId || 'all');
    const agentName =
      agentKey === 'all'
        ? 'All Employees'
        : agentKey === 'unknown'
          ? 'Unknown Agent'
          : agentIdToNameMap.get(agentKey) || `Agent ${agentKey}`;

    const key = String(empAuditDrilldownFilter || 'all').toLowerCase();
    const label =
      key === 'pending'
        ? 'Pending Audits'
        : key === 'audited'
          ? 'Audited Calls'
          : key === 'pass'
            ? 'Pass Audits'
            : key === 'needs_improvement'
              ? 'Needs Improvement Audits'
              : key === 'fail'
                ? 'Fail Audits'
                : 'Calls';
    return `${label} — ${agentName}`;
  }, [empAuditDrilldownAgentId, empAuditDrilldownFilter, agentIdToNameMap]);

  const getAudioUrl = (call) => {
    const rec = call?.recordings?.[0];
    if (!rec?.file) return null;
    return `https://s-ct3.sarv.com/v2/recording/direct/11965974${rec.file}`;
  };

  const drainAutoQueue = () => {
    if (autoActiveRef.current >= 1) return; // concurrency 1
    const next = autoQueueRef.current.shift();
    if (!next) return;
    if (autoInFlightRef.current.has(next.callid)) return drainAutoQueue();

    autoActiveRef.current += 1;
    autoInFlightRef.current.add(next.callid);

    (async () => {
      const callId = next.callid;
      try {
        setAutoTranscribeLastError('');
        const marked = await transcriptionService.markTranscriptionStatus(callId, 'processing');
        // SAFETY: if we can't update DB, do NOT call OpenAI (prevents burning credits with no saved result)
        if (!marked?.success) {
          throw new Error(`Cannot update call_reports (RLS/permission). ${marked?.error || ''}`.trim());
        }

        const audioUrl = getAudioUrl(next);
        if (!audioUrl) throw new Error('No recording URL');

        const t = await transcriptionService.transcribeAudio(audioUrl);
        if (!t.success) throw new Error(t.error || 'Transcription failed');

        const s = await transcriptionService.generateSummary(t.transcription);
        if (!s.success) throw new Error(s.error || 'Summary failed');

        const saved = await transcriptionService.saveTranscription(callId, t.transcription, s.summary, 'completed');
        if (!saved.success) throw new Error(saved.error || 'Save failed');

        setAutoTranscribeLastSuccessAt(new Date().toISOString());
        refetch();
      } catch (e) {
        const msg = e?.message || String(e);
        console.warn('❌ Auto-transcribe failed for', callId, msg);
        setAutoTranscribeLastError(msg);
        const lowerMsg = (msg || '').toLowerCase();
        // If OpenAI quota/rate-limit hit, pause auto-transcribe to avoid spamming failures/bills
        if (lowerMsg.includes('429') || lowerMsg.includes('quota')) {
          setAutoTranscribeEnabled(false);
          localStorage.setItem('callReport:autoTranscribe', 'false');
          autoQueueRef.current = [];
        }
        // Do NOT auto-retry after failure; store error on the row for visibility
        await transcriptionService.markTranscriptionStatus(callId, 'failed', { transcription_error: msg });
        refetch();
      } finally {
        autoActiveRef.current -= 1;
        autoInFlightRef.current.delete(callId);
        drainAutoQueue();
      }
    })();
  };

  const enqueueAutoTranscribe = (call) => {
    if (!call?.callid) return;
    if (autoInFlightRef.current.has(call.callid)) return;
    // Mark as seen immediately so we never auto-retry this call unless user clicks manually.
    autoSeenCallIdsRef.current.add(call.callid);
    autoQueueRef.current.push(call);
    drainAutoQueue();
  };

  const handleTranscriptionSuccess = () => {
    // Refetch data to show updated transcription/green button
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading call reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Call Report</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Monitor and analyze call statistics</p>
        </div>
        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
          {showAIChat && (
            <button
              onClick={() => setIsAISummaryOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center space-x-2"
              title="Ask AI insights from current call logs"
            >
              <Sparkles size={18} />
              <span>AI Chat</span>
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="w-full sm:w-auto px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
          >
            <BarChart3 size={18} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters + KPI Summary + Breakdown (desktop side-by-side, mobile stacked) */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-6">
        {/* Left column (stack) */}
        <div className="xl:col-span-7 flex flex-col gap-4">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Filter size={20} className="text-gray-500" />
              <h2 className="text-base md:text-lg font-semibold text-gray-900">Filters</h2>
            </div>

            {/* Agent mapping banner (Advisor/Executive) */}
            {scopeToCurrentUser && !agentCallReportId && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <div className="font-semibold">Call Report mapping missing</div>
                <div className="text-xs mt-1">
                  Please set <code className="font-mono">users.call_report_id</code> (example: <code className="font-mono">28749</code>) for this Advisor/Executive.
                  It must match the agent id stored inside <code className="font-mono">call_reports.aansh</code>.
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date &amp; Time</label>
                <button
                  type="button"
                  onClick={openDateTimeModal}
                  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white text-left hover:bg-gray-50"
                  title="Select date & time range"
                >
                  {displayLocalDateTime(dateFilter.startDate, dateFilter.startTime)}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date &amp; Time</label>
                <button
                  type="button"
                  onClick={openDateTimeModal}
                  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white text-left hover:bg-gray-50"
                  title="Select date & time range"
                >
                  {displayLocalDateTime(dateFilter.endDate, dateFilter.endTime)}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="answered">Answered</option>
                  <option value="unanswered">Unanswered</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Type</label>
                <select
                  value={callTypeFilter}
                  onChange={(e) => setCallTypeFilter(e.target.value)}
                  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="IBD">Inbound (IBD)</option>
                  <option value="OBD">Outbound (OBD)</option>
                  <option value="CTC">Click to Call (CTC)</option>
                  <option value="QC">Quick Call (QC)</option>
                </select>
              </div>
            </div>

            {/* Auto Transcribe Toggle */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
              <div className="text-sm text-gray-700">
                <span className="font-semibold">Auto Transcribe New Calls:</span>{' '}
                <span className={autoTranscribeEnabled ? 'text-green-700' : 'text-gray-500'}>
                  {autoTranscribeEnabled ? 'ON' : 'OFF'}
                </span>
                <div className="text-xs text-gray-500 mt-1">
                  New calls with recordings will auto-transcribe and save to DB (no button click).
                </div>
                {autoTranscribeEnabled && autoTranscribeLastError && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-2">
                    Auto-transcribe error: {autoTranscribeLastError}
                  </div>
                )}
                {autoTranscribeEnabled && autoTranscribeLastSuccessAt && (
                  <div className="mt-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded p-2">
                    Last auto-transcribe success: {new Date(autoTranscribeLastSuccessAt).toLocaleString('en-IN')}
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setAutoTranscribeLastError('');
                  setAutoTranscribeLastSuccessAt('');
                  setAutoTranscribeEnabled((v) => {
                    const next = !v;
                    if (next) {
                      // Reset baseline so we only process calls arriving after this point.
                      autoSeenCallIdsRef.current = new Set();
                      autoSeenInitializedRef.current = false;
                      // reset queue
                      autoQueueRef.current = [];
                      autoInFlightRef.current = new Set();
                      autoActiveRef.current = 0;
                    }
                    return next;
                  });
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  autoTranscribeEnabled
                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
                title="Toggle auto transcription"
              >
                {autoTranscribeEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
          </div>

          {/* Audit Summary (Advisor view: read-only) */}
          {!isAdminUser && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center space-x-2 mb-4">
                <ClipboardCheck size={20} className="text-gray-500" />
                <h2 className="text-base md:text-lg font-semibold text-gray-900">Audit Summary</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => openAuditDrilldown('all')}
                  className="text-left rounded-lg border border-gray-100 bg-gray-50 p-3 hover:bg-gray-100 transition"
                  title="View audited calls"
                >
                  <div className="text-xs text-gray-500">Audited Calls</div>
                  <div className="text-xl font-bold text-gray-900 mt-1">{auditSummary.totalAudited}</div>
                </button>
                <button
                  type="button"
                  onClick={() => openAuditDrilldown('pass')}
                  className="text-left rounded-lg border border-green-100 bg-green-50 p-3 hover:bg-green-100 transition"
                  title="View pass audits"
                >
                  <div className="text-xs text-green-700">Pass</div>
                  <div className="text-xl font-bold text-green-800 mt-1">{auditSummary.pass}</div>
                </button>
                <button
                  type="button"
                  onClick={() => openAuditDrilldown('needs_improvement')}
                  className="text-left rounded-lg border border-amber-100 bg-amber-50 p-3 hover:bg-amber-100 transition"
                  title="View needs-improvement audits"
                >
                  <div className="text-xs text-amber-700">Needs Improvement</div>
                  <div className="text-xl font-bold text-amber-800 mt-1">{auditSummary.needs}</div>
                </button>
                <button
                  type="button"
                  onClick={() => openAuditDrilldown('fail')}
                  className="text-left rounded-lg border border-red-100 bg-red-50 p-3 hover:bg-red-100 transition"
                  title="View fail audits"
                >
                  <div className="text-xs text-red-700">Fail</div>
                  <div className="text-xl font-bold text-red-800 mt-1">{auditSummary.fail}</div>
                </button>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                Avg. Score:{' '}
                <span className="font-semibold text-gray-900">
                  {auditSummary.avgScore === null ? '—' : `${auditSummary.avgScore.toFixed(1)}/5`}
                </span>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Tip: Recent Calls me <span className="font-semibold text-gray-700">View Audit</span> se admin ka feedback dekh sakte ho.
              </div>
            </div>
          )}

          {/* Employee-wise breakdown (fits under Filters, avoids empty gap) */}
          {employeeBreakdown && employeeBreakdown.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} className="text-teal-600" />
                <h3 className="text-sm md:text-base font-semibold text-gray-900">Employee-wise Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Fresh Call</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Repeat Call</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Linked</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employeeBreakdown.map((emp, idx) => (
                      <tr key={emp.agentId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.name}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyEmployeePerformanceFilterToRecentCalls(String(emp.agentId), 'fresh_calls')}
                            className="text-teal-600 font-semibold hover:underline"
                            title="Show this employee's fresh calls in Recent Calls"
                          >
                            {emp.freshCalls}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyEmployeePerformanceFilterToRecentCalls(String(emp.agentId), 'repeat_calls')}
                            className="text-emerald-600 font-semibold hover:underline"
                            title="Show this employee's repeat calls in Recent Calls"
                          >
                            {emp.repeatCalls}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyEmployeePerformanceFilterToRecentCalls(String(emp.agentId), 'linked')}
                            className="text-indigo-600 font-semibold hover:underline"
                            title="Show this employee's linked calls in Recent Calls"
                          >
                            {emp.linkedComplaint}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyEmployeePerformanceFilterToRecentCalls(String(emp.agentId), 'all')}
                            className="text-gray-900 font-bold hover:underline"
                            title="Show this employee's calls in Recent Calls"
                          >
                            {emp.total}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Employee-wise audit summary (admin only) */}
          {isAdminUser && auditEmployeeBreakdown?.rows?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <ClipboardCheck size={18} className="text-indigo-600" />
                  <h3 className="text-sm md:text-base font-semibold text-gray-900">Employee-wise Audit Summary</h3>
                </div>
                <button
                  type="button"
                  onClick={() => applyAuditSummaryFilterToRecentCalls('all', 'pending')}
                  className="text-xs text-gray-600 hover:text-gray-900"
                  title="View all pending audits"
                >
                  Pending:{' '}
                  <span className="font-semibold text-gray-900">
                    {auditEmployeeBreakdown.totals?.pending ?? Math.max(0, (allCallsForAudit?.length || 0) - (auditSummary?.totalAudited || 0))}
                  </span>
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => applyAuditSummaryFilterToRecentCalls('all', 'total')}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-left hover:bg-gray-100 transition"
                  title="View all calls"
                >
                  <div className="text-[11px] text-gray-600">Total Calls</div>
                  <div className="text-lg font-bold text-gray-900">{auditEmployeeBreakdown.totals?.total || 0}</div>
                </button>
                <button
                  type="button"
                  onClick={() => applyAuditSummaryFilterToRecentCalls('all', 'audited')}
                  className="rounded-lg border border-green-100 bg-green-50 p-3 text-left hover:bg-green-100 transition"
                  title="View all audited calls"
                >
                  <div className="text-[11px] text-green-700">Audited</div>
                  <div className="text-lg font-bold text-green-800">{auditEmployeeBreakdown.totals?.audited || 0}</div>
                </button>
                <button
                  type="button"
                  onClick={() => applyAuditSummaryFilterToRecentCalls('all', 'pending')}
                  className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-left hover:bg-amber-100 transition"
                  title="View all pending audits"
                >
                  <div className="text-[11px] text-amber-700">Pending</div>
                  <div className="text-lg font-bold text-amber-800">{auditEmployeeBreakdown.totals?.pending || 0}</div>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Audited</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Pending</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Pass</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Needs</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Fail</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Avg</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {auditEmployeeBreakdown.rows.map((r, idx) => (
                      <tr key={`${r.agentId}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.name}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'total')}
                            className="text-gray-900 font-bold hover:underline"
                            title="View all calls"
                          >
                            {r.total}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'audited')}
                            className="text-green-700 font-semibold hover:underline"
                            title="View audited calls"
                          >
                            {r.audited}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'pending')}
                            className="text-amber-700 font-semibold hover:underline"
                            title="View pending audits"
                          >
                            {r.pending}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'pass')}
                            className="text-green-700 hover:underline"
                            title="View pass audits"
                          >
                            {r.pass}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'needs_improvement')}
                            className="text-amber-700 hover:underline"
                            title="View needs-improvement audits"
                          >
                            {r.needs}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <button
                            type="button"
                            onClick={() => applyAuditSummaryFilterToRecentCalls(String(r.agentId), 'fail')}
                            className="text-red-700 hover:underline"
                            title="View fail audits"
                          >
                            {r.fail}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-center text-gray-900">
                          {r.avgScore === null ? '—' : `${r.avgScore.toFixed(1)}/5`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Pending = audit fields empty (no audit done yet) for calls in the selected date range.
              </div>
            </div>
          )}
        </div>

        {/* KPI Summary */}
        <div className="xl:col-span-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base md:text-lg font-semibold text-gray-900">Summary (selected date)</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Based on current filters and date range
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500">
                <Calendar size={16} />
                <span className="whitespace-nowrap">
                  {displayLocalDateTime(dateFilter.startDate, dateFilter.startTime)} → {displayLocalDateTime(dateFilter.endDate, dateFilter.endTime)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => toggleKpiFilter('fresh_calls')}
                className={`text-left rounded-lg border p-3 transition ${
                  kpiFilterKey === 'fresh_calls'
                    ? 'border-teal-300 bg-teal-50 ring-1 ring-teal-200'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                title="Show Fresh Calls (first per number)"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Fresh Calls</p>
                  <div className="p-2 bg-teal-50 rounded-lg">
                    <Phone size={16} className="text-teal-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-2">{rangeKpis.freshCalls}</p>
                <p className="text-[11px] text-gray-500 mt-1">First call per number (in range)</p>
              </button>

              <button
                type="button"
                onClick={() => toggleKpiFilter('repeat_calls')}
                className={`text-left rounded-lg border p-3 transition ${
                  kpiFilterKey === 'repeat_calls'
                    ? 'border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                title="Show Repeat Calls"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Repeat Calls</p>
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <History size={16} className="text-emerald-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 mt-2">{rangeKpis.repeatCalls}</p>
                <p className="text-[11px] text-gray-500 mt-1">Same number called again</p>
              </button>

              <div className={`rounded-lg border border-gray-200 p-3 ${kpiFilterKey === 'transcribe_completed' || kpiFilterKey === 'transcribe_failed' ? 'ring-1 ring-indigo-200 border-indigo-300 bg-indigo-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Transcribe</p>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <FileText size={16} className="text-indigo-600" />
                  </div>
                </div>
                <div className="mt-2 flex items-end justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => toggleKpiFilter('transcribe_completed')}
                    className="text-left rounded px-1 py-0.5 hover:bg-white/60"
                    title="Show Transcribe Completed"
                  >
                    <p className="text-[11px] text-gray-500">Completed</p>
                    <p className="text-xl font-bold text-green-600">{rangeKpis.transcribeCompleted}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleKpiFilter('transcribe_failed')}
                    className="text-right rounded px-1 py-0.5 hover:bg-white/60"
                    title="Show Transcribe Failed"
                  >
                    <p className="text-[11px] text-gray-500">Failed</p>
                    <p className="text-xl font-bold text-red-600">{rangeKpis.transcribeFailed}</p>
                  </button>
                </div>
              </div>

              <div className={`rounded-lg border border-gray-200 p-3 ${kpiFilterKey === 'fresh_not_linked' || kpiFilterKey === 'linked' ? 'ring-1 ring-yellow-200 border-yellow-300 bg-yellow-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500">Fresh / Linked</p>
                  <div className="p-2 bg-yellow-50 rounded-lg">
                    <ExternalLink size={16} className="text-yellow-700" />
                  </div>
                </div>
                <div className="mt-2 flex items-end justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => toggleKpiFilter('fresh_not_linked')}
                    className="text-left rounded px-1 py-0.5 hover:bg-white/60"
                    title="Show Fresh (Not linked)"
                  >
                    <p className="text-[11px] text-gray-500">Fresh</p>
                    <p className="text-xl font-bold text-gray-900">{rangeKpis.freshNotLinked}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleKpiFilter('linked')}
                    className="text-right rounded px-1 py-0.5 hover:bg-white/60"
                    title="Show Linked"
                  >
                    <p className="text-[11px] text-gray-500">Linked</p>
                    <p className="text-xl font-bold text-teal-700">{rangeKpis.freshLinked}</p>
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Fresh = not linked to complaint</p>
              </div>
            </div>

            {stats && (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => toggleKpiFilter('answered')}
                  className={`text-left rounded-lg border p-3 transition ${
                    kpiFilterKey === 'answered'
                      ? 'border-green-300 bg-green-50 ring-1 ring-green-200'
                      : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                  }`}
                  title="Show Answered Calls"
                >
                  <p className="text-xs text-gray-500">Answered</p>
                  <p className="text-xl font-bold text-green-700 mt-1">{stats.answeredCalls}</p>
                </button>
                <button
                  type="button"
                  onClick={() => toggleKpiFilter('all')}
                  className={`text-left rounded-lg border p-3 transition ${
                    kpiFilterKey === 'all'
                      ? 'border-gray-300 bg-white ring-1 ring-gray-200'
                      : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                  }`}
                  title="Show All Calls"
                >
                  <p className="text-xs text-gray-500">Avg. Duration</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{formatDuration(stats.averageDuration)}</p>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Calls (full width on desktop to avoid right-side blank space while scrolling) */}
        <div className="xl:col-span-12" ref={recentCallsSectionRef}>
          {/* Call List - Mobile Responsive (Cards on Mobile, Table on Desktop) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 md:mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-gray-900">Recent Calls</h2>
            {kpiFilterKey !== 'all' && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs bg-teal-50 border border-teal-100 text-teal-800 rounded px-2 py-1">
                <span className="font-semibold">Filter:</span>
                <span>{kpiFilterLabel}</span>
                <button
                  type="button"
                  onClick={() => toggleKpiFilter('all')}
                  className="ml-1 text-teal-700 hover:text-teal-900 underline"
                >
                  Clear
                </button>
              </div>
            )}
            {(recentCallsFiltered?.length || 0) > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Showing <span className="font-semibold text-gray-700">{pageStartNumber}-{pageEndNumber}</span> of{' '}
                <span className="font-semibold text-gray-700">{totalCallsCount}</span>
              </p>
            )}
          </div>

          {/* Records limit selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Show</span>
            <select
              value={recordsLimit}
              onChange={(e) => setRecordsLimit(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              title="Records to display"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">records</span>
          </div>
        </div>
        {enableRecentCallsFilters && (
          <div className="mb-4 grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <input
                value={recentSearch}
                onChange={(e) => setRecentSearch(e.target.value)}
                placeholder="Search: phone, agent, city, vehicle, callid…"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <select
                value={recentLinkedFilter}
                onChange={(e) => setRecentLinkedFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                title="Linked filter"
              >
                <option value="all">All (Linked + Not linked)</option>
                <option value="linked">Linked</option>
                <option value="not_linked">Not linked</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <select
                value={recentAgentFilter}
                onChange={(e) => setRecentAgentFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                title="Agent filter"
              >
                <option value="all">All Agents</option>
                {recentAgentOptions.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <select
                value={recentAuditFilter}
                onChange={(e) => setRecentAuditFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                title="Audit filter"
              >
                <option value="all">Audit: All</option>
                <option value="pending">Audit: Pending</option>
                <option value="audited">Audit: Audited</option>
                <option value="pass">Audit: Pass</option>
                <option value="needs_improvement">Audit: Needs</option>
                <option value="fail">Audit: Fail</option>
              </select>
            </div>
          </div>
        )}
        {!recentCallsFiltered || recentCallsFiltered.length === 0 ? (
          <div className="text-center py-12">
            <Phone size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-sm md:text-base text-gray-500">No call reports found for the selected filters</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View - Visible only on mobile */}
            <div className="block md:hidden space-y-3">
              {visibleCalls.map((call, index) => {
                const linkedSr = getLinkedServiceRequest(call);
                const linkedId = linkedSr?.id;
                const agentName = getAgentNameForCall(call);
                const isHighlighted = String(call?.callid || '') && String(call?.callid || '') === String(recentHighlightCallId || '');
                return (
                <div
                  id={call?.callid ? `recent-call-${String(call.callid)}` : undefined}
                  key={call.callid || index}
                  className={`border rounded-lg p-4 space-y-3 transition ${
                    isHighlighted ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200' : 'border-gray-200'
                  }`}
                >
                  {/* Type */}
                  <div className="flex items-start justify-end">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs whitespace-nowrap">
                      {getCallTypeLabel(call.ctype || 'Unknown')}
                    </span>
                  </div>

                  {/* Customer & Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Customer</p>
                      {call.cnumber ? (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-gray-900">
                            {formatPhoneNumber(call.cnumber)}
                          </p>
                          {agentName ? (
                            <p className="text-[11px] text-gray-500">
                              Agent: <span className="font-medium text-gray-700">{agentName}</span>
                            </p>
                          ) : null}
                          {call.city ? (
                            <p className="text-[11px] text-gray-500">
                              City: <span className="font-medium text-gray-700">{String(call.city)}</span>
                            </p>
                          ) : null}
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleCopyPhone(call.cnumber)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Copy"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => handleCallNumber(call.cnumber)}
                              className="text-teal-500 hover:text-teal-700"
                              title="Call"
                            >
                              <Phone size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">N/A</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Duration</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDuration(call.talkduration || call.ivrduration)}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div>
                    <span className={`inline-block px-3 py-1 rounded text-xs font-medium ${getStatusColor(call.callstatus)}`}>
                      {getStatusLabel(call.callstatus)}
                    </span>
                  </div>

                  {/* Recording */}
                  {call.recordings && call.recordings.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Recording</p>
                      <CallRecordingPlayer
                        src={getAudioUrl(call)}
                        className="w-full"
                        compact={false}
                        showDownload={false}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleTranscribeClick(call)}
                      className={`flex-1 min-w-[120px] flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        call.summary
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      {call.summary ? (
                        <>
                          <CheckCircle size={14} />
                          <span>View Summary</span>
                        </>
                      ) : (
                        <>
                          <FileText size={14} />
                          <span>Transcribe</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => handleDispositionClick(call)}
                      className={`flex-1 min-w-[120px] flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        call.disposition_category
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <ClipboardList size={14} />
                      <span>{call.disposition_category ? 'View Note' : 'Add Note'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => openDetails(call)}
                      className="flex-1 min-w-[120px] flex items-center justify-center px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                      title="View full call details"
                    >
                      Details
                    </button>
                  </div>

                  {/* Auto-transcribe error (no auto-retry; manual click required) */}
                  {String(call.transcription_status || '').toLowerCase() === 'failed' && call.transcription_error && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <span className="font-semibold">Auto Transcribe Failed:</span>{' '}
                      <span>{call.transcription_error}</span>
                      <div className="text-[11px] text-red-600 mt-1">
                        Retry only by clicking <span className="font-semibold">Transcribe</span>.
                      </div>
                    </div>
                  )}

                  {/* Previous noted indicator (same complaint / same number) */}
                  {hasPreviousNoteForCall(call) && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
                      <History size={14} className="text-emerald-600" />
                      <span className="font-medium">Previous noted</span>
                      <span className="text-emerald-600">— last call note available</span>
                    </div>
                  )}

                  {/* Linked Complaint (by service_request_id OR by phone match) */}
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-500 mb-1">Linked Complaint</p>
                      {enableRecentCallsFilters ? (
                        <button
                          type="button"
                          onClick={() => handleAuditClick(call)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                          title="Audit & Feedback"
                        >
                          <ClipboardCheck size={12} />
                          <span>{call.audit_updated_at || call.audit_status || call.audit_feedback ? 'Edit Audit' : 'Audit'}</span>
                        </button>
                      ) : (call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) ? (
                        <button
                          type="button"
                          onClick={() => handleAuditClick(call)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                          title="View Audit"
                        >
                          <ClipboardCheck size={12} />
                          <span>View Audit</span>
                        </button>
                      ) : null}
                    </div>
                    {linkedSr && linkedId ? (
                      <div className="space-y-1">
                        <button
                          onClick={() => navigate(`${complaintDetailsPathPrefix}/${linkedId}`)}
                          className="flex items-center space-x-1 text-teal-600 hover:text-teal-700 font-medium text-sm"
                        >
                          <span>{linkedSr.customer_name || 'Unknown'}</span>
                          <ExternalLink size={12} />
                        </button>
                        {linkedSr.vehicle_number && (
                          <p className="text-xs text-gray-600">
                            🚗 {linkedSr.vehicle_number}
                          </p>
                        )}
                        {linkedSr.complaint_status && (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              linkedSr.complaint_status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : linkedSr.complaint_status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : linkedSr.complaint_status === 'cancelled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {linkedSr.complaint_status}
                          </span>
                        )}
                      </div>
                    ) : (call.disposition || call.disposition_category || call.price || call.service_type || call.notes_detail) ? (
                      <div className="space-y-1">
                        <span className="text-xs text-gray-400">Not linked</span>
                        <span className={`block px-2 py-1 rounded text-xs font-medium ${
                          (call.disposition || call.disposition_category) === 'cancelled' || (call.disposition || call.disposition_category) === 'cancelled_by_customer' ? 'bg-red-100 text-red-700' :
                          (call.disposition || call.disposition_category) === 'spam' ? 'bg-red-100 text-red-700' :
                          (call.disposition || call.disposition_category) === 'follow_up' || (call.disposition || call.disposition_category) === 'follow_up_required' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {(call.disposition || call.disposition_category).toString().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        {(call.service_type) && (
                          <p className="text-xs text-gray-600">Service: {call.service_type}</p>
                        )}
                        {(call.price !== null && call.price !== undefined && call.price !== '') && (
                          <p className="text-xs text-green-700 font-medium">Price: ₹{call.price}</p>
                        )}
                        {(call.notes_detail || call.disposition_note) && (
                          <p className="text-xs text-gray-500 italic">"{(call.notes_detail || call.disposition_note).substring(0, 50)}..."</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Not linked</span>
                    )}
                    {(call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) && (
                      <div className="mt-2 text-[11px] text-gray-500">
                        Audit:{' '}
                        <span className="font-medium text-gray-700">
                          {call.audit_status ? String(call.audit_status).replace(/_/g, ' ') : 'Saved'}
                          {call.audit_score ? ` • ${call.audit_score}/5` : ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Time */}
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      {formatDateTime(call.createdat || call.ivrstime)}
                    </p>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Desktop Table View - Hidden on mobile */}
            <div className="hidden md:block rounded-lg border border-gray-200 overflow-hidden">
              <div className="max-h-[70vh] overflow-auto">
              <table className="min-w-[1050px] w-full table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  <th className="w-[6.5rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Type</th>
                  <th className="w-[12rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Customer</th>
                  <th className="w-[11rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Status</th>
                  <th className="w-[5.5rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Duration</th>
                  <th className="w-[11.5rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Recording</th>
                  <th className="w-[10.5rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                  <th className="w-[12rem] text-left py-3 pl-2 pr-1 text-xs font-semibold text-gray-700 whitespace-nowrap">Linked</th>
                  <th className="w-[5.5rem] text-left py-3 pl-1 pr-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Audit</th>
                  <th className="w-[8.5rem] text-left py-3 px-2 text-xs font-semibold text-gray-700 whitespace-nowrap">Time</th>
                </tr>
              </thead>
              <tbody>
                {visibleCalls.map((call, index) => {
                  const linkedSr = getLinkedServiceRequest(call);
                  const linkedId = linkedSr?.id;
                  const agentName = getAgentNameForCall(call);
                  const isHighlighted = String(call?.callid || '') && String(call?.callid || '') === String(recentHighlightCallId || '');
                  return (
                  <tr
                    id={call?.callid ? `recent-call-${String(call.callid)}` : undefined}
                    key={call.callid || index}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      isHighlighted ? 'bg-indigo-50 outline outline-2 outline-indigo-200' : ''
                    }`}
                  >
                    <td className="py-2 px-2 text-xs text-gray-700 whitespace-nowrap">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {getCallTypeLabel(call.ctype || 'Unknown')}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-700">
                      {call.cnumber ? (
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{formatPhoneNumber(call.cnumber)}</span>
                            <button
                              onClick={() => handleCopyPhone(call.cnumber)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy phone number"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              onClick={() => handleCallNumber(call.cnumber)}
                              className="text-teal-500 hover:text-teal-700"
                              title="Call customer"
                            >
                              <Phone size={14} />
                            </button>
                          </div>
                          {agentName ? (
                            <div className="mt-0.5 text-[11px] text-gray-500 truncate" title={agentName}>
                              Agent: <span className="font-medium text-gray-700">{agentName}</span>
                            </div>
                          ) : null}
                          {call.city ? (
                            <div className="text-[11px] text-gray-500 truncate" title={String(call.city)}>
                              City: <span className="font-medium text-gray-700">{String(call.city)}</span>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(call.callstatus)}`}>
                        {getStatusLabel(call.callstatus)}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-700">
                      {formatDuration(call.talkduration || call.ivrduration)}
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500">
                      {call.recordings && call.recordings.length > 0 ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <CallRecordingPlayer
                            src={getAudioUrl(call)}
                            compact={true}
                            className="min-w-0 w-full max-w-[200px]"
                          />
                          {/* Check if already transcribed */}
                          {call.transcription || call.summary ? (
                            <button
                              onClick={() => handleTranscribeClick(call)}
                              className="p-1.5 bg-green-100 hover:bg-green-200 rounded text-green-700 hover:text-green-800 transition-colors shadow-sm shadow-green-300 border border-green-300"
                              title="View AI Summary (Already Transcribed)"
                            >
                              <FileText size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleTranscribeClick(call)}
                              className="p-1.5 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-700 transition-colors"
                              title="Transcribe & Summarize Call"
                            >
                              <FileText size={16} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">No recording</span>
                      )}
                      {String(call.transcription_status || '').toLowerCase() === 'failed' && call.transcription_error && (
                        <div className="mt-1 text-[11px] text-red-700">
                          <div>Auto failed (manual Transcribe to retry)</div>
                          <div className="line-clamp-2 break-words text-[11px] text-red-600">
                            {call.transcription_error}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-xs">
                      <div className="flex flex-col space-y-2">
                        {/* Transcribe Button */}
                        <button
                          onClick={() => handleTranscribeClick(call)}
                          className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            call.summary
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          }`}
                          title={call.summary ? 'View/Edit Summary' : 'Transcribe & Summarize'}
                        >
                          {call.summary ? (
                            <>
                              <CheckCircle size={14} />
                              <span>View Summary</span>
                            </>
                          ) : (
                            <>
                              <FileText size={14} />
                              <span>Transcribe</span>
                            </>
                          )}
                        </button>
                        
                        {/* Disposition Button */}
                        <button
                          onClick={() => handleDispositionClick(call)}
                          className={`flex items-center justify-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            call.disposition_category
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={call.disposition_category ? 'View/Edit Disposition' : 'Add Disposition'}
                        >
                          <ClipboardList size={14} />
                          <span>{call.disposition_category ? 'View Note' : 'Add Note'}</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => openDetails(call)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                          title="View full call details"
                        >
                          Details
                        </button>

                        {/* Previous noted indicator (same complaint / same number) */}
                        {hasPreviousNoteForCall(call) && (
                          <div
                            className="inline-flex items-center justify-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                            title="Same complaint/number has an earlier note — opening will auto-fill"
                          >
                            <History size={12} className="text-emerald-600" />
                            <span className="font-medium">Previous noted</span>
                          </div>
                        )}
                        
                        {/* Status Indicators */}
                        <div className="flex items-center space-x-2 text-xs">
                          {call.summary && (
                            <div className="text-green-600 flex items-center space-x-1">
                              <MessageSquare size={12} />
                              <span>AI Summary</span>
                            </div>
                          )}
                          {call.disposition_category && (
                            <div className="text-blue-600 flex items-center space-x-1">
                              <ClipboardList size={12} />
                              <span>Noted</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pl-2 pr-1 text-xs">
                      {linkedSr && linkedId ? (
                        <div className="space-y-1">
                          <button
                            onClick={() => navigate(`${complaintDetailsPathPrefix}/${linkedId}`)}
                            className="flex items-center space-x-1 text-teal-600 hover:text-teal-700 font-semibold"
                          >
                            <span>{linkedSr.customer_name || 'Unknown'}</span>
                            <ExternalLink size={12} />
                          </button>
                          <div className="text-xs text-gray-600 space-y-0.5">
                            {linkedSr.vehicle_number && (
                              <div className="flex items-center space-x-1">
                                <span className="font-medium">🚗</span>
                                <span>{linkedSr.vehicle_number}</span>
                              </div>
                            )}
                            {linkedSr.problem && (
                              <div className="text-gray-500 break-words line-clamp-2" title={linkedSr.problem}>
                                {String(linkedSr.problem)}
                              </div>
                            )}
                            {linkedSr.complaint_status && (
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                linkedSr.complaint_status === 'completed' ? 'bg-green-100 text-green-700' :
                                linkedSr.complaint_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                linkedSr.complaint_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {linkedSr.complaint_status}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <span className="text-gray-400 text-xs block">Not linked</span>
                          {(call.disposition || call.disposition_category || call.price || call.service_type || call.notes_detail || call.disposition_note) && (
                            <div className="space-y-1">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                (call.disposition || call.disposition_category) === 'linked_to_complaint' ? 'bg-green-100 text-green-700' :
                                (call.disposition || call.disposition_category) === 'cancelled' || (call.disposition || call.disposition_category) === 'cancelled_by_customer' ? 'bg-red-100 text-red-700' :
                                (call.disposition || call.disposition_category) === 'spam' ? 'bg-red-100 text-red-700' :
                                (call.disposition || call.disposition_category) === 'test_call' ? 'bg-gray-100 text-gray-700' :
                                (call.disposition || call.disposition_category) === 'follow_up' || (call.disposition || call.disposition_category) === 'follow_up_required' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {(call.disposition || call.disposition_category || 'not_linked').toString().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              {call.service_type && (
                                <div className="text-xs text-gray-600">Service: {call.service_type}</div>
                              )}
                              {(call.price !== null && call.price !== undefined && call.price !== '') && (
                                <div className="text-xs text-green-700 font-medium">Price: ₹{call.price}</div>
                              )}
                              {(call.notes_detail || call.disposition_note) && (
                                <div className="text-xs text-gray-500 italic truncate max-w-xs" title={call.notes_detail || call.disposition_note}>
                                  "{(call.notes_detail || call.disposition_note).substring(0, 40)}{(call.notes_detail || call.disposition_note).length > 40 ? '...' : ''}"
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pl-1 pr-2 text-xs">
                      <div className="flex flex-col gap-1">
                        {(call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) ? (
                          <button
                            type="button"
                            onClick={() => handleAuditClick(call)}
                            className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                            title={enableRecentCallsFilters ? 'Audit & Feedback' : 'View Audit'}
                          >
                            <ClipboardCheck size={14} />
                            <span className="text-xs font-medium">
                              {enableRecentCallsFilters
                                ? (call.audit_updated_at || call.audit_status || call.audit_feedback ? 'Edit' : 'Audit')
                                : 'View'}
                            </span>
                          </button>
                        ) : (
                          enableRecentCallsFilters ? (
                            <button
                              type="button"
                              onClick={() => handleAuditClick(call)}
                              className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                              title="Audit & Feedback"
                            >
                              <ClipboardCheck size={14} />
                              <span className="text-xs font-medium">Audit</span>
                            </button>
                          ) : (
                            <span className="text-[11px] text-gray-400">—</span>
                          )
                        )}
                        {(call.audit_updated_at || call.audit_status || call.audit_score) && (
                          <div className="text-[11px] text-gray-500 truncate" title={call.audit_feedback || ''}>
                            {call.audit_status ? String(call.audit_status).replace(/_/g, ' ') : 'Audited'}
                            {call.audit_score ? ` • ${call.audit_score}/5` : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-xs text-gray-500 whitespace-nowrap">
                      {formatDateTime(call.createdat || call.ivrstime)}
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
            </div>
          </div>
          </>
        )}

        {/* Pagination Controls */}
        {totalCallsCount > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
            <div className="text-xs text-gray-500">
              Page <span className="font-semibold text-gray-700">{safeCurrentPage}</span> of{' '}
              <span className="font-semibold text-gray-700">{totalPages}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safeCurrentPage <= 1}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                  safeCurrentPage <= 1
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safeCurrentPage >= totalPages}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                  safeCurrentPage >= totalPages
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Date & Time Modal (easier than native datetime pickers) */}
      {showDateTimeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-6" role="dialog" aria-modal="true">
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Select Date &amp; Time</h2>
                <p className="text-xs text-gray-500 mt-0.5">Filter calls by created time range.</p>
              </div>
              <button onClick={closeDateTimeModal} className="p-2 rounded-lg hover:bg-gray-100" title="Close">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setPreset('today')} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Today
                </button>
                <button onClick={() => setPreset('yesterday')} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Yesterday
                </button>
                <button onClick={() => setPreset('last7')} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Last 7 Days
                </button>
              </div>

              {dateTimeError ? (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle size={16} className="mt-0.5" />
                  <div>{dateTimeError}</div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">Start</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <input
                        type="date"
                        value={toLocalYMD(dateTimeDraft.start)}
                        onChange={(e) => {
                          const next = localDateTimeFromParts(e.target.value, toLocalHM(dateTimeDraft.start), dateTimeDraft.start);
                          if (next) setDateTimeDraft((d) => ({ ...d, start: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                      <input
                        type="time"
                        value={toLocalHM(dateTimeDraft.start)}
                        onChange={(e) => {
                          const next = localDateTimeFromParts(toLocalYMD(dateTimeDraft.start), e.target.value, dateTimeDraft.start);
                          if (next) setDateTimeDraft((d) => ({ ...d, start: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">End</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <input
                        type="date"
                        value={toLocalYMD(dateTimeDraft.end)}
                        onChange={(e) => {
                          const next = localDateTimeFromParts(e.target.value, toLocalHM(dateTimeDraft.end), dateTimeDraft.end);
                          if (next) setDateTimeDraft((d) => ({ ...d, end: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                      <input
                        type="time"
                        value={toLocalHM(dateTimeDraft.end)}
                        onChange={(e) => {
                          const next = localDateTimeFromParts(toLocalYMD(dateTimeDraft.end), e.target.value, dateTimeDraft.end);
                          if (next) setDateTimeDraft((d) => ({ ...d, end: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
              <button onClick={closeDateTimeModal} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white">
                Cancel
              </button>
              <button
                onClick={() => {
                  const s = dateTimeDraft?.start instanceof Date ? dateTimeDraft.start : null;
                  const e = dateTimeDraft?.end instanceof Date ? dateTimeDraft.end : null;
                  if (!s || Number.isNaN(s.getTime()) || !e || Number.isNaN(e.getTime())) {
                    setDateTimeError('Please select valid start and end date/time.');
                    return;
                  }
                  const start = s.getTime() <= e.getTime() ? s : e;
                  const end = s.getTime() <= e.getTime() ? e : s;
                  setDateFilter((prev) => ({
                    ...prev,
                    startDate: toLocalYMD(start),
                    startTime: toLocalHM(start),
                    endDate: toLocalYMD(end),
                    endTime: toLocalHM(end),
                  }));
                  setShowDateTimeModal(false);
                  setDateTimeError('');
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Details Modal */}
      {isDetailsModalOpen && selectedCallForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3 py-6" role="dialog" aria-modal="true">
          <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gray-50">
              <div className="min-w-0">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 truncate">Call Details</h2>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {formatDateTime(selectedCallForDetails.createdat || selectedCallForDetails.ivrstime)}
                  {selectedCallForDetails.callid ? ` • CallID: ${String(selectedCallForDetails.callid)}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedCallForDetails(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {(() => {
              const call = selectedCallForDetails;
              const linkedSr = getLinkedServiceRequest(call);
              const linkedId = linkedSr?.id;
              const agentName = getAgentNameForCall(call);
              const audioUrl = getAudioUrl(call);
              return (
                <div className="p-4 md:p-6 overflow-y-auto max-h-[80vh] space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Call</div>
                      <div className="text-xs text-gray-700 space-y-1">
                        <div>
                          <span className="text-gray-500">Type:</span>{' '}
                          <span className="font-medium">{getCallTypeLabel(call.ctype || call.cType || 'Unknown')}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <span className="font-medium">{getStatusLabel(call.callstatus ?? call.callStatus)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Duration:</span>{' '}
                          <span className="font-medium">{formatDuration(call.talkduration || call.ivrduration)}</span>
                        </div>
                        {call.city ? (
                          <div>
                            <span className="text-gray-500">City:</span>{' '}
                            <span className="font-medium">{String(call.city)}</span>
                          </div>
                        ) : null}
                        {agentName ? (
                          <div>
                            <span className="text-gray-500">Agent:</span>{' '}
                            <span className="font-medium">{agentName}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Customer</div>
                      <div className="text-xs text-gray-700 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {call.cnumber ? formatPhoneNumber(call.cnumber) : 'N/A'}
                          </span>
                          {call.cnumber ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleCopyPhone(call.cnumber)}
                                className="px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                                title="Copy"
                              >
                                Copy
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCallNumber(call.cnumber)}
                                className="px-2 py-1 rounded bg-teal-600 hover:bg-teal-700 text-white"
                                title="Call"
                              >
                                Call
                              </button>
                            </>
                          ) : null}
                        </div>
                        {linkedSr && linkedId ? (
                          <div className="pt-2 border-t border-gray-100">
                            <div className="text-[11px] text-gray-500 mb-1">Linked Complaint</div>
                            <button
                              type="button"
                              onClick={() => navigate(`${complaintDetailsPathPrefix}/${linkedId}`)}
                              className="text-teal-700 hover:text-teal-900 font-semibold text-sm inline-flex items-center gap-1"
                              title="Open complaint"
                            >
                              <span className="truncate">{linkedSr.customer_name || 'Open complaint'}</span>
                              <ExternalLink size={14} />
                            </button>
                            {linkedSr.vehicle_number ? (
                              <div className="text-[11px] text-gray-600 mt-1">Vehicle: {String(linkedSr.vehicle_number)}</div>
                            ) : null}
                            {linkedSr.problem ? (
                              <div className="text-[11px] text-gray-600 mt-1 line-clamp-3" title={String(linkedSr.problem)}>
                                Problem: {String(linkedSr.problem)}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="pt-2 border-t border-gray-100 text-[11px] text-gray-500">
                            Linked Complaint: <span className="font-medium text-gray-700">Not linked</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Recording</div>
                      {audioUrl ? (
                        <CallRecordingPlayer src={audioUrl} className="w-full" compact={false} showDownload={true} />
                      ) : (
                        <div className="text-xs text-gray-500">No recording</div>
                      )}
                    </div>

                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Disposition / Notes</div>
                      <div className="text-xs text-gray-700 space-y-1">
                        {call.service_type ? (
                          <div>
                            <span className="text-gray-500">Service:</span>{' '}
                            <span className="font-medium">{String(call.service_type)}</span>
                          </div>
                        ) : null}
                        {call.price !== null && call.price !== undefined && call.price !== '' ? (
                          <div>
                            <span className="text-gray-500">Price:</span>{' '}
                            <span className="font-medium text-green-700">₹{String(call.price)}</span>
                          </div>
                        ) : null}
                        {(call.disposition || call.disposition_category) ? (
                          <div>
                            <span className="text-gray-500">Disposition:</span>{' '}
                            <span className="font-medium">
                              {String(call.disposition || call.disposition_category).replace(/_/g, ' ')}
                            </span>
                          </div>
                        ) : null}
                        {(call.notes_detail || call.disposition_note) ? (
                          <div className="pt-1 text-gray-600 whitespace-pre-wrap break-words">
                            "{String(call.notes_detail || call.disposition_note)}"
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">No notes</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-900">Audit</div>
                      {enableRecentCallsFilters ? (
                        <button
                          type="button"
                          onClick={() => handleAuditClick(call)}
                          className="px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                        >
                          {call.audit_updated_at || call.audit_status || call.audit_feedback ? 'Edit Audit' : 'Audit'}
                        </button>
                      ) : (call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) ? (
                        <button
                          type="button"
                          onClick={() => handleAuditClick(call)}
                          className="px-3 py-2 rounded-lg text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
                        >
                          View Audit
                        </button>
                      ) : null}
                    </div>
                    {(call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) ? (
                      <div className="mt-2 text-xs text-gray-700 space-y-1">
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <span className="font-medium">{call.audit_status ? String(call.audit_status).replace(/_/g, ' ') : 'Audited'}</span>
                          {call.audit_score ? <span className="font-medium"> • {String(call.audit_score)}/5</span> : null}
                        </div>
                        {call.audit_feedback ? (
                          <div className="text-gray-600 whitespace-pre-wrap break-words">"{String(call.audit_feedback)}"</div>
                        ) : (
                          <div className="text-gray-500">No feedback</div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No audit saved</div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Transcription Modal */}
      {selectedCallForTranscription && isTranscriptionModalOpen && (
        <TranscriptionModal
          call={selectedCallForTranscription}
          onClose={() => {
            setIsTranscriptionModalOpen(false);
            setSelectedCallForTranscription(null);
          }}
          onSave={handleTranscriptionSuccess}
        />
      )}

      {/* Disposition Modal */}
      {selectedCallForDisposition && isDispositionModalOpen && (
        <DispositionModal
          call={selectedCallForDisposition}
          open={isDispositionModalOpen}
          onClose={() => {
            setIsDispositionModalOpen(false);
            setSelectedCallForDisposition(null);
          }}
          onSave={handleDispositionSave}
        />
      )}

      {/* Audit Modal (Admin can edit; Advisor can view) */}
      {selectedCallForAudit && isAuditModalOpen && (
        <CallAuditModal
          open={isAuditModalOpen}
          call={selectedCallForAudit}
          agentName={getAgentNameForCall(selectedCallForAudit)}
          readOnly={!enableRecentCallsFilters}
          onClose={() => {
            setIsAuditModalOpen(false);
            setSelectedCallForAudit(null);
          }}
          onSave={handleAuditSave}
        />
      )}

      {/* Audit Drilldown Modal (Advisor only): click Audit Summary cards to see customer details */}
      {!isAdminUser && isAuditDrilldownOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
            <div className="bg-gray-900 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-base md:text-xl font-bold truncate">{drilldownTitle}</div>
                <div className="text-xs md:text-sm text-gray-300 truncate">
                  Showing <span className="font-semibold text-white">{drilldownCalls.length}</span> calls
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAuditDrilldownOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-10 rounded-lg p-2"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(95vh-90px)]">
              {drilldownCalls.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-500">No audited calls found.</div>
              ) : (
                <div className="space-y-3">
                  {drilldownCalls.map((call, idx) => {
                    const linkedSr = getLinkedServiceRequest(call);
                    const linkedId = linkedSr?.id;
                    const agentName = getAgentNameForCall(call);
                    return (
                      <div
                        key={call.callid || idx}
                        className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition"
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Customer</div>
                            <div className="text-sm font-semibold text-gray-900">
                              {linkedSr?.customer_name || (call?.cnumber ? formatPhoneNumber(call.cnumber) : 'N/A')}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Phone: <span className="font-medium">{call?.cnumber ? formatPhoneNumber(call.cnumber) : 'N/A'}</span>
                              {agentName ? (
                                <>
                                  {' '}
                                  • Agent: <span className="font-medium">{agentName}</span>
                                </>
                              ) : null}
                              {' '}
                              • Time: <span className="font-medium">{formatDateTime(call.createdat || call.ivrstime)}</span>
                            </div>

                            {linkedSr && (
                              <div className="mt-2 text-xs text-gray-700 space-y-1">
                                {linkedSr.vehicle_number && (
                                  <div>
                                    Vehicle: <span className="font-medium">{linkedSr.vehicle_number}</span>
                                  </div>
                                )}
                                {linkedSr.problem && (
                                  <div className="text-gray-600 line-clamp-2" title={linkedSr.problem}>
                                    Problem: <span className="font-medium text-gray-700">{String(linkedSr.problem)}</span>
                                  </div>
                                )}
                                {linkedSr.complaint_status && (
                                  <div>
                                    Status:{' '}
                                    <span className="font-medium text-gray-800">{String(linkedSr.complaint_status)}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {(call.audit_status || call.audit_score) && (
                              <div className="mt-2 text-xs text-gray-600">
                                Audit:{' '}
                                <span className="font-semibold text-gray-800">
                                  {call.audit_status ? String(call.audit_status).replace(/_/g, ' ') : 'Audited'}
                                  {call.audit_score ? ` • ${call.audit_score}/5` : ''}
                                </span>
                              </div>
                            )}
                            {call.audit_feedback && (
                              <div className="mt-1 text-xs text-gray-600 italic line-clamp-2" title={call.audit_feedback}>
                                "{String(call.audit_feedback)}"
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {linkedId && (
                              <button
                                type="button"
                                onClick={() => {
                                  // Close drilldown first so navigation/next modal isn't hidden behind it
                                  setIsAuditDrilldownOpen(false);
                                  setTimeout(() => navigate(`${complaintDetailsPathPrefix}/${linkedId}`), 0);
                                }}
                                className="px-3 py-2 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 inline-flex items-center gap-1"
                                title="Open linked complaint"
                              >
                                <ExternalLink size={14} />
                                Complaint
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                // Close drilldown first so Audit modal is not rendered behind it
                                setIsAuditDrilldownOpen(false);
                                setTimeout(() => handleAuditClick(call), 0);
                              }}
                              className="px-3 py-2 rounded-lg text-xs font-semibold border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 inline-flex items-center gap-1"
                              title="View audit"
                            >
                              <ClipboardCheck size={14} />
                              View Audit
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employee-wise Audit Drilldown (Admin): click numbers in audit summary to view calls */}
      {isAdminUser && isEmpAuditDrilldownOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
            <div className="bg-indigo-700 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-base md:text-xl font-bold truncate">{empAuditDrilldownTitle}</div>
                <div className="text-xs md:text-sm text-indigo-100 truncate">
                  Showing <span className="font-semibold text-white">{empAuditDrilldownCalls.length}</span> calls
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEmpAuditDrilldownOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-10 rounded-lg p-2"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(95vh-90px)]">
              {empAuditDrilldownCalls.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-500">No calls found.</div>
              ) : (
                <div className="space-y-3">
                  {empAuditDrilldownCalls.map((call, idx) => {
                    const linkedSr = getLinkedServiceRequest(call);
                    const linkedId = linkedSr?.id;
                    const agentName = getAgentNameForCall(call);
                    const phone = call?.cnumber ? formatPhoneNumber(call.cnumber) : 'N/A';
                    return (
                      <div
                        key={call.callid || idx}
                        className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition"
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Customer</div>
                            <div className="text-sm font-semibold text-gray-900">
                              {linkedSr?.customer_name || phone}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Phone:{' '}
                              <button
                                type="button"
                                onClick={() => handleCopyPhone(call?.cnumber || '')}
                                className="font-medium hover:underline"
                                title="Copy phone"
                              >
                                {phone}
                              </button>
                              {agentName ? (
                                <>
                                  {' '}
                                  • Agent: <span className="font-medium">{agentName}</span>
                                </>
                              ) : null}
                              {' '}
                              • Time: <span className="font-medium">{formatDateTime(call.createdat || call.ivrstime)}</span>
                            </div>

                            {linkedSr && (
                              <div className="mt-2 text-xs text-gray-700 space-y-1">
                                {linkedSr.vehicle_number && (
                                  <div>
                                    Vehicle: <span className="font-medium">{linkedSr.vehicle_number}</span>
                                  </div>
                                )}
                                {linkedSr.problem && (
                                  <div className="text-gray-600 line-clamp-2" title={linkedSr.problem}>
                                    Problem: <span className="font-medium text-gray-700">{String(linkedSr.problem)}</span>
                                  </div>
                                )}
                                {linkedSr.complaint_status && (
                                  <div>
                                    Status:{' '}
                                    <span className="font-medium text-gray-800">{String(linkedSr.complaint_status)}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {(call.audit_updated_at || call.audit_status || call.audit_score || call.audit_feedback) ? (
                              <div className="mt-2 text-xs text-gray-600">
                                Audit:{' '}
                                <span className="font-semibold text-gray-800">
                                  {call.audit_status ? String(call.audit_status).replace(/_/g, ' ') : 'Audited'}
                                  {call.audit_score ? ` • ${call.audit_score}/5` : ''}
                                </span>
                              </div>
                            ) : (
                              <div className="mt-2 text-xs text-amber-700 font-semibold">Pending audit</div>
                            )}
                            {call.audit_feedback && (
                              <div className="mt-1 text-xs text-gray-600 italic line-clamp-2" title={call.audit_feedback}>
                                "{String(call.audit_feedback)}"
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {linkedId && (
                              <button
                                type="button"
                                onClick={() => jumpToRecentCall(call)}
                                className="px-3 py-2 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 inline-flex items-center gap-1"
                                title="Open linked complaint"
                              >
                                <ExternalLink size={14} />
                                Complaint
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => jumpToRecentCall(call)}
                              className="px-3 py-2 rounded-lg text-xs font-semibold border border-gray-300 bg-white hover:bg-gray-50 text-gray-800 inline-flex items-center gap-1"
                              title="View details"
                            >
                              <FileText size={14} />
                              Details
                            </button>
                            <button
                              type="button"
                              onClick={() => jumpToRecentCall(call)}
                              className="px-3 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1"
                              title="Audit / Edit audit"
                            >
                              <ClipboardCheck size={14} />
                              Audit
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Summary / Insights Modal */}
      {showAIChat && isAISummaryOpen && (
        <AISummaryModal
          open={isAISummaryOpen}
          onClose={() => setIsAISummaryOpen(false)}
          filters={{
            dateFilter,
            statusFilter,
            callTypeFilter,
            scopeToCurrentUser,
            agentCallReportId,
          }}
        />
      )}
    </div>
  );
};

export default CallReport;

